import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const MEAL_OPTIONS = [
  { value: 'steak', label: 'Steak' },
  { value: 'shrimp', label: 'Shrimp' },
  { value: 'vegetarian', label: 'Vegetarian' },
];

export default function AdminPage() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginData, setLoginData] = useState({ username: '', password: '' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [rsvps, setRsvps] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Guest List State
  const [guestList, setGuestList] = useState({ households: [] });
  const [householdRsvps, setHouseholdRsvps] = useState([]);
  const [contactRequests, setContactRequests] = useState([]);
  const [editingHousehold, setEditingHousehold] = useState(null);
  const [guestSearch, setGuestSearch] = useState('');
  const [guestStatusFilter, setGuestStatusFilter] = useState('all');
  const [guestImportFile, setGuestImportFile] = useState(null);
  const [guestImportPreview, setGuestImportPreview] = useState(null);
  const [guestImportLoading, setGuestImportLoading] = useState(false);
  const [manualRsvpHouseholdId, setManualRsvpHouseholdId] = useState(null);
  const [manualRsvp, setManualRsvp] = useState(null);
  const [householdEmailAction, setHouseholdEmailAction] = useState(null);
  const [newHousehold, setNewHousehold] = useState({
    label: '',
    allows_plus_one: false,
    members: [{ first_name: '', last_name: '', email: '' }]
  });
  
  // Site Settings State
  const [siteSettings, setSiteSettings] = useState({
    show_rsvp: false,
    show_registry: false
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    const restoreAdminSession = async () => {
      const stored = sessionStorage.getItem('adminCreds');
      if (!stored) return;

      try {
        const credentials = JSON.parse(stored);
        const authHeader = 'Basic ' + btoa(`${credentials.username}:${credentials.password}`);
        await axios.get(`${API}/admin/verify`, {
          headers: { Authorization: authHeader },
        });
        setLoginData(credentials);
        setIsAuthenticated(true);
      } catch (error) {
        sessionStorage.removeItem('adminAuth');
        sessionStorage.removeItem('adminCreds');
      }
    };

    restoreAdminSession();
  }, []);

  const getAuthHeader = () => {
    const stored = sessionStorage.getItem('adminCreds');
    if (stored) {
      const { username, password } = JSON.parse(stored);
      return 'Basic ' + btoa(`${username}:${password}`);
    }
    if (loginData.username && loginData.password) {
      return 'Basic ' + btoa(`${loginData.username}:${loginData.password}`);
    }
    return '';
  };

  // Fetch data when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
    // fetchData is intentionally invoked only when authentication changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);

    try {
      await axios.post(`${API}/admin/login`, loginData);
      sessionStorage.setItem('adminAuth', 'true');
      sessionStorage.setItem('adminCreds', JSON.stringify(loginData));
      setIsAuthenticated(true);
      toast.success('Login successful');
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    sessionStorage.removeItem('adminCreds');
    setIsAuthenticated(false);
    setLoginData({ username: '', password: '' });
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const authHeader = getAuthHeader();
      
      const [rsvpsRes, statsRes, guestListRes, householdRsvpsRes, settingsRes, contactRes] = await Promise.all([
        axios.get(`${API}/rsvp`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/rsvp/stats`, { headers: { Authorization: authHeader } }),
        axios.get(`${API}/admin/guest-list`, { headers: { Authorization: authHeader } }).catch(() => ({ data: { households: [] } })),
        axios.get(`${API}/admin/household-rsvps`, { headers: { Authorization: authHeader } }).catch(() => ({ data: [] })),
        axios.get(`${API}/settings`).catch(() => ({ data: { show_rsvp: false, show_registry: false } })),
        axios.get(`${API}/admin/contact-requests`, { headers: { Authorization: authHeader } }).catch(() => ({ data: [] })),
      ]);
      setRsvps(rsvpsRes.data);
      setStats(statsRes.data);
      setGuestList(guestListRes.data);
      setHouseholdRsvps(householdRsvpsRes.data);
      setSiteSettings(settingsRes.data);
      setContactRequests(contactRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  // Update Site Settings
  const handleUpdateSettings = async (field, value) => {
    setSettingsLoading(true);
    try {
      const authHeader = getAuthHeader();
      await axios.patch(`${API}/admin/settings`, 
        { [field]: value },
        { headers: { Authorization: authHeader } }
      );
      setSiteSettings(prev => ({ ...prev, [field]: value }));
      toast.success(`${field === 'show_rsvp' ? 'RSVP' : 'Registry'} ${value ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error('Failed to update settings');
      // Revert on error
      setSiteSettings(prev => ({ ...prev, [field]: !value }));
    } finally {
      setSettingsLoading(false);
    }
  };
  
  // Guest List Functions
  const addMemberField = () => {
    setNewHousehold(prev => ({
      ...prev,
      members: [...prev.members, { first_name: '', last_name: '', email: '' }]
    }));
  };
  
  const removeMemberField = (index) => {
    setNewHousehold(prev => ({
      ...prev,
      members: prev.members.filter((_, i) => i !== index)
    }));
  };
  
  const updateMemberField = (index, field, value) => {
    setNewHousehold(prev => ({
      ...prev,
      members: prev.members.map((m, i) => i === index ? { ...m, [field]: value } : m)
    }));
  };
  
  const generateHouseholdId = (label) => {
    return label.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-');
  };

  const resetHouseholdForm = () => {
    setEditingHousehold(null);
    setNewHousehold({
      label: '',
      allows_plus_one: false,
      members: [{ first_name: '', last_name: '', email: '' }]
    });
  };

  const filteredHouseholds = useMemo(() => {
    const query = guestSearch.trim().toLowerCase();
    return (guestList.households || []).filter((household) => {
      const hasResponded = householdRsvps.some(
        (rsvp) => rsvp.household_id === household.id
      );
      if (guestStatusFilter === 'awaiting' && hasResponded) return false;
      if (guestStatusFilter === 'responded' && !hasResponded) return false;
      if (!query) return true;

      const searchable = [
        household.label,
        ...(household.members || []).flatMap((member) => [
          member.first_name,
          member.last_name,
          member.email,
        ]),
      ].filter(Boolean).join(' ').toLowerCase();

      return searchable.includes(query);
    });
  }, [guestList.households, householdRsvps, guestSearch, guestStatusFilter]);

  const awaitingHouseholds = useMemo(
    () => (guestList.households || []).filter(
      (household) => !householdRsvps.some(
        (rsvp) => rsvp.household_id === household.id
      )
    ),
    [guestList.households, householdRsvps]
  );

  const previewGuestImport = async (file) => {
    if (!file) return;
    setGuestImportFile(file);
    setGuestImportPreview(null);
    setGuestImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(
        `${API}/admin/guest-list/import?preview=true`,
        formData,
        { headers: { Authorization: getAuthHeader() } }
      );
      setGuestImportPreview(response.data);
    } catch (error) {
      setGuestImportFile(null);
      toast.error(error.response?.data?.detail || 'We could not read that guest list.');
    } finally {
      setGuestImportLoading(false);
    }
  };

  const importGuestList = async () => {
    if (!guestImportFile) return;
    setGuestImportLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', guestImportFile);
      const response = await axios.post(
        `${API}/admin/guest-list/import?preview=false`,
        formData,
        { headers: { Authorization: getAuthHeader() } }
      );
      toast.success(
        `${response.data.guests_to_add} guest${response.data.guests_to_add === 1 ? '' : 's'} imported`
      );
      setGuestImportFile(null);
      setGuestImportPreview(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Guest list import failed.');
    } finally {
      setGuestImportLoading(false);
    }
  };

  const openManualRsvp = (household) => {
    if (manualRsvpHouseholdId === household.id) {
      setManualRsvpHouseholdId(null);
      setManualRsvp(null);
      return;
    }

    const existing = householdRsvps.find(
      (rsvp) => rsvp.household_id === household.id
    );
    setManualRsvpHouseholdId(household.id);
    setManualRsvp({
      household_id: household.id,
      responses: (household.members || []).map((member) => {
        const saved = existing?.responses?.find(
          (response) => response.member_id === member.id
        );
        return {
          member_id: member.id,
          first_name: member.first_name,
          last_name: member.last_name,
          attendance: saved?.attendance || '',
          welcome_party: saved?.welcome_party || '',
          meal_choice: saved?.meal_choice || '',
          dietary_restrictions: saved?.dietary_restrictions || '',
        };
      }),
      include_plus_one: Boolean(existing?.plus_one),
      plus_one: {
        name: existing?.plus_one?.name || '',
        attendance: existing?.plus_one?.attendance || '',
        welcome_party: existing?.plus_one?.welcome_party || '',
        meal_choice: existing?.plus_one?.meal_choice || '',
        dietary_restrictions: existing?.plus_one?.dietary_restrictions || '',
      },
      contact_email:
        existing?.contact_email ||
        household.members?.find((member) => member.email)?.email ||
        '',
      contact_phone: existing?.contact_phone || '',
      submitted_by: existing?.submitted_by || 'Admin entry',
    });
  };

  const updateManualResponse = (index, field, value) => {
    setManualRsvp((current) => ({
      ...current,
      responses: current.responses.map((response, responseIndex) => {
        if (index !== responseIndex) return response;
        const next = { ...response, [field]: value };
        if (field === 'attendance' && value === 'decline') next.meal_choice = '';
        return next;
      }),
    }));
  };

  const updateManualPlusOne = (field, value) => {
    setManualRsvp((current) => {
      const plusOne = { ...current.plus_one, [field]: value };
      if (field === 'attendance' && value === 'decline') plusOne.meal_choice = '';
      return { ...current, plus_one: plusOne };
    });
  };

  const saveManualRsvp = async () => {
    if (!manualRsvp) return;
    for (const response of manualRsvp.responses) {
      const name = `${response.first_name} ${response.last_name}`;
      if (!response.attendance || !response.welcome_party) {
        toast.error(`Choose both event responses for ${name}.`);
        return;
      }
      if (response.attendance === 'accept' && !response.meal_choice) {
        toast.error(`Choose a meal for ${name}.`);
        return;
      }
    }
    if (manualRsvp.include_plus_one) {
      const plusOne = manualRsvp.plus_one;
      if (!plusOne.name.trim() || !plusOne.attendance || !plusOne.welcome_party) {
        toast.error('Complete the guest name and both event responses.');
        return;
      }
      if (plusOne.attendance === 'accept' && !plusOne.meal_choice) {
        toast.error('Choose a meal for the additional guest.');
        return;
      }
    }

    try {
      await axios.put(
        `${API}/admin/household-rsvps/${manualRsvp.household_id}`,
        {
          household_id: manualRsvp.household_id,
          responses: manualRsvp.responses,
          plus_one: manualRsvp.include_plus_one ? manualRsvp.plus_one : null,
          contact_email: manualRsvp.contact_email.trim() || null,
          contact_phone: manualRsvp.contact_phone.trim() || null,
          submitted_by: manualRsvp.submitted_by.trim() || 'Admin entry',
        },
        { headers: { Authorization: getAuthHeader() } }
      );
      toast.success('RSVP saved');
      setManualRsvpHouseholdId(null);
      setManualRsvp(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'The RSVP could not be saved.');
    }
  };

  const sendHouseholdEmail = async (householdId, type) => {
    const actionKey = `${householdId}-${type}`;
    setHouseholdEmailAction(actionKey);
    try {
      const endpoint = type === 'confirmation'
        ? `${API}/admin/household-rsvps/${householdId}/resend-confirmation`
        : `${API}/admin/households/${householdId}/send-reminder`;
      const response = await axios.post(endpoint, null, {
        headers: { Authorization: getAuthHeader() },
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'The email could not be sent.');
    } finally {
      setHouseholdEmailAction(null);
    }
  };
  
  const handleAddHousehold = async () => {
    if (!newHousehold.label.trim()) {
      toast.error('Please enter a household name');
      return;
    }
    
    const validMembers = newHousehold.members.filter(m => m.first_name.trim() && m.last_name.trim());
    if (validMembers.length === 0) {
      toast.error('Please add at least one member');
      return;
    }
    
    const householdId = editingHousehold || generateHouseholdId(newHousehold.label);
    const household = {
      id: householdId,
      label: newHousehold.label.trim(),
      allows_plus_one: newHousehold.allows_plus_one,
      max_plus_ones: newHousehold.allows_plus_one ? 1 : 0,
      members: validMembers.map((m, i) => ({
        id: m.id || `${householdId}-member-${i}`,
        first_name: m.first_name.trim(),
        last_name: m.last_name.trim(),
        email: m.email.trim() || null
      }))
    };
    
    try {
      const method = editingHousehold ? 'put' : 'post';
      const endpoint = editingHousehold
        ? `${API}/admin/guest-list/household/${editingHousehold}`
        : `${API}/admin/guest-list/household`;
      await axios[method](endpoint, household, {
        headers: { Authorization: getAuthHeader() }
      });
      toast.success(editingHousehold ? 'Household updated' : 'Household added');
      resetHouseholdForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${editingHousehold ? 'update' : 'add'} household`);
    }
  };

  const handleEditHousehold = (household) => {
    setEditingHousehold(household.id);
    setNewHousehold({
      label: household.label,
      allows_plus_one: Boolean(household.allows_plus_one),
      members: (household.members || []).map((member) => ({
        id: member.id,
        first_name: member.first_name || '',
        last_name: member.last_name || '',
        email: member.email || '',
      })),
    });
    window.scrollTo({ top: 340, behavior: 'smooth' });
  };
  
  const handleDeleteHousehold = async (householdId) => {
    if (!window.confirm('Delete this household? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/admin/guest-list/household/${householdId}`, {
        headers: { Authorization: getAuthHeader() }
      });
      toast.success('Household deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete household');
    }
  };
  
  const getHouseholdRsvpStatus = (householdId) => {
    const rsvp = householdRsvps.find(r => r.household_id === householdId);
    if (!rsvp) return null;
    
    const attending = rsvp.responses.filter(r => r.attendance === 'accept').length;
    const plusOne = rsvp.plus_one?.attendance === 'accept' ? 1 : 0;
    return { attending: attending + plusOne, total: rsvp.responses.length + (rsvp.plus_one ? 1 : 0), rsvp };
  };

  const handleDeleteRsvp = async (rsvp) => {
    const householdResponse = Boolean(rsvp.household_rsvp_id);
    const warning = householdResponse
      ? 'Delete this household RSVP? All responses for the household will be removed.'
      : 'Delete this RSVP? This cannot be undone.';
    if (!window.confirm(warning)) return;
    
    try {
      await axios.delete(`${API}/rsvp/${rsvp.id}`, {
        headers: { Authorization: getAuthHeader() },
      });
      toast.success('RSVP deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete RSVP');
    }
  };

  const handleUpdateMessageStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/contact-requests/${id}`, { status }, {
        headers: { Authorization: getAuthHeader() },
      });
      setContactRequests((requests) => requests.map((request) => (
        request.id === id ? { ...request, status } : request
      )));
      toast.success(status === 'resolved' ? 'Message marked resolved' : 'Message reopened');
    } catch (error) {
      toast.error('Failed to update message');
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Delete this guest message? This cannot be undone.')) return;

    try {
      await axios.delete(`${API}/admin/contact-requests/${id}`, {
        headers: { Authorization: getAuthHeader() },
      });
      setContactRequests((requests) => requests.filter((request) => request.id !== id));
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/rsvp/export`, {
        headers: { Authorization: getAuthHeader() },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'rsvps.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to export RSVPs');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div data-testid="admin-login-page" className="admin-login-page min-h-screen">
        <div className="admin-login-card max-w-md mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-[0.25em] text-olive mb-4">Admin Access</p>
            <h1 className="admin-display text-4xl md:text-5xl text-forest">Dashboard Login</h1>
            <p className="admin-login-card__intro">Manage your guest list, responses, messages, and website settings.</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-8" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs uppercase tracking-[0.15em] text-olive">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
                data-testid="admin-username-input"
                className="bg-transparent border-0 border-b border-forest/30 rounded-none px-0 py-3 text-forest placeholder:text-forest/40 focus:border-forest focus-visible:ring-0 focus-visible:ring-offset-0"
                placeholder="Enter username"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] text-olive">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                data-testid="admin-password-input"
                className="bg-transparent border-0 border-b border-forest/30 rounded-none px-0 py-3 text-forest placeholder:text-forest/40 focus:border-forest focus-visible:ring-0 focus-visible:ring-offset-0"
                placeholder="Enter password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={isLoggingIn}
              data-testid="admin-login-btn"
              className="btn-primary w-full disabled:opacity-50"
            >
              {isLoggingIn ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Authenticated Dashboard
  return (
    <div data-testid="admin-page" className="admin-page min-h-screen">
      {/* Header */}
      <section className="admin-header max-w-7xl mx-auto">
        <div className="admin-header__inner">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-olive mb-4">Dashboard</p>
            <h1 className="admin-display text-4xl md:text-5xl text-forest">Wedding Admin</h1>
            <p className="admin-header__intro">Guest list, responses, questions, and page visibility in one place.</p>
          </div>
          <div className="admin-header__actions">
            <a href="/" target="_blank" rel="noreferrer" className="btn-outline">View website</a>
            <button onClick={fetchData} className="btn-outline" disabled={loading}>
              {loading ? 'Refreshing…' : 'Refresh data'}
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="admin-text-action"
            >
              Log out
            </button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="admin-dashboard max-w-7xl mx-auto">
        <Tabs defaultValue="guests" className="w-full">
          <TabsList className="admin-tabs mb-8 bg-transparent border-b border-forest/20 rounded-none w-full justify-start gap-8 h-auto pb-0">
            <TabsTrigger 
              value="guests" 
              data-testid="tab-guests"
              className="text-sm uppercase tracking-[0.15em] text-forest/60 data-[state=active]:text-forest data-[state=active]:border-b-2 data-[state=active]:border-forest rounded-none pb-4 px-0 bg-transparent"
            >
              Guest List
            </TabsTrigger>
            <TabsTrigger 
              value="rsvps" 
              data-testid="tab-rsvps"
              className="text-sm uppercase tracking-[0.15em] text-forest/60 data-[state=active]:text-forest data-[state=active]:border-b-2 data-[state=active]:border-forest rounded-none pb-4 px-0 bg-transparent"
            >
              RSVPs
            </TabsTrigger>
            <TabsTrigger
              value="messages"
              data-testid="tab-messages"
              className="text-sm uppercase tracking-[0.15em] text-forest/60 data-[state=active]:text-forest data-[state=active]:border-b-2 data-[state=active]:border-forest rounded-none pb-4 px-0 bg-transparent"
            >
              Guest Messages
              {contactRequests.length > 0 && ` (${contactRequests.length})`}
            </TabsTrigger>
            <TabsTrigger 
              value="settings" 
              data-testid="tab-settings"
              className="text-sm uppercase tracking-[0.15em] text-forest/60 data-[state=active]:text-forest data-[state=active]:border-b-2 data-[state=active]:border-forest rounded-none pb-4 px-0 bg-transparent"
            >
              Settings
            </TabsTrigger>
          </TabsList>
          
          {/* Guest List Tab */}
          <TabsContent value="guests">
            <div className="space-y-8">
              {/* Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{guestList.households?.length || 0}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Households</p>
                </div>
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">
                    {guestList.households?.reduce((sum, h) => sum + (h.members?.length || 0), 0) || 0}
                  </p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Total Guests</p>
                </div>
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{householdRsvps.length}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">RSVPs Received</p>
                </div>
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">
                    {awaitingHouseholds.length}
                  </p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Awaiting Response</p>
                </div>
              </div>

              <div className="editorial-card admin-import-card">
                <div className="admin-section-heading">
                  <div>
                    <p className="eyebrow">Guest list upload</p>
                    <h3 className="admin-display text-2xl text-forest">
                      Import CSV or Excel (.xlsx)
                    </h3>
                  </div>
                  <a
                    href="/templates/guest-import-template.csv"
                    download
                    className="admin-text-action"
                  >
                    Download template
                  </a>
                </div>
                <p className="admin-import-card__copy">
                  Use one row per guest. Guests with the same household name will
                  share one invitation. Nothing is saved until you review the preview.
                </p>
                <div className="admin-import-card__controls">
                  <label className="btn-outline admin-file-picker">
                    <input
                      type="file"
                      accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onClick={(event) => {
                        event.currentTarget.value = '';
                      }}
                      onChange={(event) => previewGuestImport(event.target.files?.[0])}
                    />
                    {guestImportLoading ? 'Reading file…' : 'Choose guest list'}
                  </label>
                  {guestImportFile && (
                    <span className="admin-import-card__filename">
                      {guestImportFile.name}
                    </span>
                  )}
                </div>

                {guestImportPreview && (
                  <div className="admin-import-preview" role="status">
                    <div className="admin-import-preview__counts">
                      <p>
                        <strong>{guestImportPreview.guests_to_add}</strong>
                        <span>Guests to add</span>
                      </p>
                      <p>
                        <strong>{guestImportPreview.households_to_add}</strong>
                        <span>New households</span>
                      </p>
                      <p>
                        <strong>{guestImportPreview.households_to_update}</strong>
                        <span>Households updated</span>
                      </p>
                      <p>
                        <strong>{guestImportPreview.duplicates.length}</strong>
                        <span>Duplicates skipped</span>
                      </p>
                    </div>

                    {(guestImportPreview.duplicates.length > 0 ||
                      guestImportPreview.invalid_rows.length > 0) && (
                      <details className="admin-import-preview__details">
                        <summary>Review skipped rows</summary>
                        <ul>
                          {guestImportPreview.duplicates.map((item) => (
                            <li key={`duplicate-${item.row}`}>
                              Row {item.row}: {item.name} — {item.reason}
                            </li>
                          ))}
                          {guestImportPreview.invalid_rows.map((item) => (
                            <li key={`invalid-${item.row}`}>
                              Row {item.row}: {item.reason}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}

                    <div className="admin-import-preview__actions">
                      <button
                        type="button"
                        onClick={importGuestList}
                        disabled={
                          guestImportLoading ||
                          guestImportPreview.guests_to_add === 0
                        }
                        className="btn-primary disabled:opacity-45"
                      >
                        {guestImportLoading ? 'Importing…' : 'Import these guests'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setGuestImportFile(null);
                          setGuestImportPreview(null);
                        }}
                        className="admin-text-action"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Add or edit household form */}
              <div className="editorial-card admin-household-form">
                <div className="admin-section-heading">
                  <div>
                    <p className="eyebrow">{editingHousehold ? 'Editing invitation' : 'New invitation'}</p>
                    <h3 className="admin-display text-2xl text-forest">
                      {editingHousehold ? 'Edit Household' : 'Add Household'}
                    </h3>
                  </div>
                  {editingHousehold && (
                    <button type="button" onClick={resetHouseholdForm} className="admin-text-action">
                      Cancel editing
                    </button>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-olive">
                      Household Name (e.g., "The Smith Family" or "John Doe")
                    </Label>
                    <Input
                      value={newHousehold.label}
                      onChange={(e) => setNewHousehold(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="The Smith Family"
                      className="mt-2 bg-transparent border border-forest/20 rounded-sm"
                    />
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="allows_plus_one"
                      checked={newHousehold.allows_plus_one}
                      onChange={(e) => setNewHousehold(prev => ({ ...prev, allows_plus_one: e.target.checked }))}
                      className="w-4 h-4 accent-forest"
                    />
                    <Label htmlFor="allows_plus_one" className="text-sm text-forest cursor-pointer">
                      Allow +1 guest (for single invitees who can bring a date)
                    </Label>
                  </div>
                  
                  <div>
                    <Label className="text-xs uppercase tracking-[0.15em] text-olive mb-3 block">
                      Members
                    </Label>
                    {newHousehold.members.map((member, index) => (
                      <div key={member.id || index} className="admin-member-row">
                        <Input
                          placeholder="First Name"
                          value={member.first_name}
                          onChange={(e) => updateMemberField(index, 'first_name', e.target.value)}
                          className="flex-1 bg-transparent border border-forest/20 rounded-sm"
                        />
                        <Input
                          placeholder="Last Name"
                          value={member.last_name}
                          onChange={(e) => updateMemberField(index, 'last_name', e.target.value)}
                          className="flex-1 bg-transparent border border-forest/20 rounded-sm"
                        />
                        <Input
                          placeholder="Email (optional)"
                          value={member.email}
                          onChange={(e) => updateMemberField(index, 'email', e.target.value)}
                          className="flex-1 bg-transparent border border-forest/20 rounded-sm"
                        />
                        {newHousehold.members.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeMemberField(index)}
                            className="admin-remove-member"
                            aria-label={`Remove ${member.first_name || 'member'}`}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addMemberField}
                      className="text-sm text-olive hover:text-forest mt-2"
                    >
                      + Add Another Member
                    </button>
                  </div>
                  
                  <button
                    onClick={handleAddHousehold}
                    className="btn-primary"
                  >
                    {editingHousehold ? 'Save Household' : 'Add Household'}
                  </button>
                </div>
              </div>
              
              {/* Existing Households */}
              <div className="editorial-card">
                <div className="admin-section-heading">
                  <div>
                    <p className="eyebrow">Invitations</p>
                    <h3 className="admin-display text-2xl text-forest">
                      Households ({filteredHouseholds.length})
                    </h3>
                  </div>
                  <div className="admin-household-tools">
                    <div className="admin-filter-pills" aria-label="Filter households">
                      {[
                        ['all', `All ${guestList.households?.length || 0}`],
                        ['awaiting', `Awaiting ${awaitingHouseholds.length}`],
                        ['responded', `Responded ${householdRsvps.length}`],
                      ].map(([value, label]) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setGuestStatusFilter(value)}
                          aria-pressed={guestStatusFilter === value}
                          className={guestStatusFilter === value ? 'is-active' : ''}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <Input
                      type="search"
                      value={guestSearch}
                      onChange={(event) => setGuestSearch(event.target.value)}
                      placeholder="Search guests"
                      aria-label="Search guests"
                      className="admin-guest-search"
                    />
                  </div>
                </div>
                
                {guestList.households?.length === 0 ? (
                  <p className="text-forest/60 text-center py-8">No households added yet.</p>
                ) : filteredHouseholds.length === 0 ? (
                  <p className="text-forest/60 text-center py-8">No guests match “{guestSearch}.”</p>
                ) : (
                  <div className="space-y-4">
                    {filteredHouseholds.map((household) => {
                      const rsvpStatus = getHouseholdRsvpStatus(household.id);
                      return (
                        <div key={household.id} className="admin-household-card">
                          <div className="admin-household-card__inner">
                            <div>
                              <div className="admin-household-card__title">
                                <h4 className="font-medium text-forest">{household.label}</h4>
                                {household.allows_plus_one && (
                                  <span className="admin-chip">+1 invited</span>
                                )}
                                {rsvpStatus && (
                                  <span className="admin-chip admin-chip--filled">
                                    Responded · {rsvpStatus.attending}/{rsvpStatus.total} attending
                                  </span>
                                )}
                              </div>
                              <div className="admin-household-card__members">
                                {household.members?.map((member) => (
                                  <p key={member.id || `${member.first_name}-${member.last_name}`}>
                                    <span>{member.first_name} {member.last_name}</span>
                                    {member.email && <small>{member.email}</small>}
                                  </p>
                                ))}
                              </div>
                            </div>
                            <div className="admin-household-card__actions">
                              <button onClick={() => handleEditHousehold(household)} className="admin-text-action">
                                Edit
                              </button>
                              <button
                                onClick={() => openManualRsvp(household)}
                                className="admin-text-action"
                              >
                                {rsvpStatus ? 'Edit RSVP' : 'Record RSVP'}
                              </button>
                              <button
                                onClick={() => sendHouseholdEmail(
                                  household.id,
                                  rsvpStatus ? 'confirmation' : 'reminder'
                                )}
                                disabled={Boolean(householdEmailAction)}
                                className="admin-text-action disabled:opacity-45"
                              >
                                {householdEmailAction === `${household.id}-${rsvpStatus ? 'confirmation' : 'reminder'}`
                                  ? 'Sending…'
                                  : rsvpStatus
                                    ? 'Resend confirmation'
                                    : 'Send reminder'}
                              </button>
                              <button onClick={() => handleDeleteHousehold(household.id)} className="admin-text-action admin-text-action--danger">
                                Delete
                              </button>
                            </div>
                          </div>
                          {manualRsvpHouseholdId === household.id && manualRsvp && (
                            <div className="admin-manual-rsvp">
                              <div className="admin-manual-rsvp__heading">
                                <div>
                                  <p className="eyebrow">
                                    {rsvpStatus ? 'Update response' : 'Manual entry'}
                                  </p>
                                  <h5 className="admin-display text-xl text-forest">
                                    RSVP for {household.label}
                                  </h5>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => openManualRsvp(household)}
                                  className="admin-text-action"
                                >
                                  Close
                                </button>
                              </div>

                              <div className="space-y-5">
                                {manualRsvp.responses.map((response, index) => (
                                  <fieldset
                                    key={response.member_id}
                                    className="admin-rsvp-person"
                                  >
                                    <legend>
                                      {response.first_name} {response.last_name}
                                    </legend>
                                    <div className="admin-rsvp-person__grid">
                                      <label>
                                        <span>Friday reception</span>
                                        <select
                                          value={response.welcome_party}
                                          onChange={(event) => updateManualResponse(
                                            index,
                                            'welcome_party',
                                            event.target.value
                                          )}
                                        >
                                          <option value="">Choose…</option>
                                          <option value="yes">Attending</option>
                                          <option value="no">Not attending</option>
                                        </select>
                                      </label>
                                      <label>
                                        <span>Saturday wedding</span>
                                        <select
                                          value={response.attendance}
                                          onChange={(event) => updateManualResponse(
                                            index,
                                            'attendance',
                                            event.target.value
                                          )}
                                        >
                                          <option value="">Choose…</option>
                                          <option value="accept">Attending</option>
                                          <option value="decline">Not attending</option>
                                        </select>
                                      </label>
                                      {response.attendance === 'accept' && (
                                        <label>
                                          <span>Meal</span>
                                          <select
                                            value={response.meal_choice}
                                            onChange={(event) => updateManualResponse(
                                              index,
                                              'meal_choice',
                                              event.target.value
                                            )}
                                          >
                                            <option value="">Choose…</option>
                                            {MEAL_OPTIONS.map((meal) => (
                                              <option key={meal.value} value={meal.value}>
                                                {meal.label}
                                              </option>
                                            ))}
                                          </select>
                                        </label>
                                      )}
                                      <label className="admin-rsvp-person__dietary">
                                        <span>Dietary notes</span>
                                        <Input
                                          value={response.dietary_restrictions}
                                          onChange={(event) => updateManualResponse(
                                            index,
                                            'dietary_restrictions',
                                            event.target.value
                                          )}
                                          placeholder="Leave blank if none"
                                        />
                                      </label>
                                    </div>
                                  </fieldset>
                                ))}

                                {household.allows_plus_one && (
                                  <div className="admin-plus-one">
                                    <label className="admin-check-row">
                                      <input
                                        type="checkbox"
                                        checked={manualRsvp.include_plus_one}
                                        onChange={(event) => setManualRsvp((current) => ({
                                          ...current,
                                          include_plus_one: event.target.checked,
                                        }))}
                                      />
                                      Include the additional guest in this response
                                    </label>
                                    {manualRsvp.include_plus_one && (
                                      <fieldset className="admin-rsvp-person">
                                        <legend>Additional guest</legend>
                                        <div className="admin-rsvp-person__grid">
                                          <label>
                                            <span>Full name</span>
                                            <Input
                                              value={manualRsvp.plus_one.name}
                                              onChange={(event) => updateManualPlusOne(
                                                'name',
                                                event.target.value
                                              )}
                                            />
                                          </label>
                                          <label>
                                            <span>Friday reception</span>
                                            <select
                                              value={manualRsvp.plus_one.welcome_party}
                                              onChange={(event) => updateManualPlusOne(
                                                'welcome_party',
                                                event.target.value
                                              )}
                                            >
                                              <option value="">Choose…</option>
                                              <option value="yes">Attending</option>
                                              <option value="no">Not attending</option>
                                            </select>
                                          </label>
                                          <label>
                                            <span>Saturday wedding</span>
                                            <select
                                              value={manualRsvp.plus_one.attendance}
                                              onChange={(event) => updateManualPlusOne(
                                                'attendance',
                                                event.target.value
                                              )}
                                            >
                                              <option value="">Choose…</option>
                                              <option value="accept">Attending</option>
                                              <option value="decline">Not attending</option>
                                            </select>
                                          </label>
                                          {manualRsvp.plus_one.attendance === 'accept' && (
                                            <label>
                                              <span>Meal</span>
                                              <select
                                                value={manualRsvp.plus_one.meal_choice}
                                                onChange={(event) => updateManualPlusOne(
                                                  'meal_choice',
                                                  event.target.value
                                                )}
                                              >
                                                <option value="">Choose…</option>
                                                {MEAL_OPTIONS.map((meal) => (
                                                  <option key={meal.value} value={meal.value}>
                                                    {meal.label}
                                                  </option>
                                                ))}
                                              </select>
                                            </label>
                                          )}
                                          <label className="admin-rsvp-person__dietary">
                                            <span>Dietary notes</span>
                                            <Input
                                              value={manualRsvp.plus_one.dietary_restrictions}
                                              onChange={(event) => updateManualPlusOne(
                                                'dietary_restrictions',
                                                event.target.value
                                              )}
                                            />
                                          </label>
                                        </div>
                                      </fieldset>
                                    )}
                                  </div>
                                )}

                                <div className="admin-manual-rsvp__contact">
                                  <label>
                                    <span>Contact email</span>
                                    <Input
                                      type="email"
                                      value={manualRsvp.contact_email}
                                      onChange={(event) => setManualRsvp((current) => ({
                                        ...current,
                                        contact_email: event.target.value,
                                      }))}
                                      placeholder="Optional for manual entry"
                                    />
                                  </label>
                                  <label>
                                    <span>Contact phone</span>
                                    <Input
                                      value={manualRsvp.contact_phone}
                                      onChange={(event) => setManualRsvp((current) => ({
                                        ...current,
                                        contact_phone: event.target.value,
                                      }))}
                                      placeholder="Optional"
                                    />
                                  </label>
                                  <label>
                                    <span>Recorded by</span>
                                    <Input
                                      value={manualRsvp.submitted_by}
                                      onChange={(event) => setManualRsvp((current) => ({
                                        ...current,
                                        submitted_by: event.target.value,
                                      }))}
                                    />
                                  </label>
                                </div>
                                <p className="admin-manual-rsvp__note">
                                  Saving a manual response does not email the guest.
                                  Use “Resend confirmation” afterward if you want to notify them.
                                </p>
                                <button
                                  type="button"
                                  onClick={saveManualRsvp}
                                  className="btn-primary"
                                >
                                  Save RSVP
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* RSVPs Tab */}
          <TabsContent value="rsvps">
            {/* RSVP Stats */}
            {stats && (
              <div 
                data-testid="rsvp-stats"
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8"
              >
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{stats.total}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Guest Responses</p>
                </div>
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{stats.attending}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Attending</p>
                </div>
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{stats.plus_ones || 0}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Plus Ones</p>
                </div>
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{stats.steak_count}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Steak</p>
                </div>
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{stats.shrimp_count}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Shrimp</p>
                </div>
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{stats.vegetarian_count || 0}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Vegetarian</p>
                </div>
              </div>
            )}

            {/* Welcome Reception Stats */}
            {stats && (
              <div 
                data-testid="welcome-party-stats"
                className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12"
              >
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{stats.welcome_party_yes || 0}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Welcome Reception Yes</p>
                </div>
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{stats.welcome_party_no || 0}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Welcome Reception No</p>
                </div>
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{stats.declining}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Declining</p>
                </div>
                <div className="editorial-card text-center">
                  <p className="admin-display text-3xl text-forest">{stats.dietary_notes}</p>
                  <p className="text-xs uppercase tracking-[0.15em] text-olive mt-2">Dietary Notes</p>
                </div>
              </div>
            )}

            {/* Export Button */}
            <div className="flex justify-end mb-8">
              <Button
                onClick={handleExport}
                data-testid="export-csv-btn"
                className="btn-outline"
              >
                Export CSV
              </Button>
            </div>

            {/* RSVP Table */}
            {loading ? (
              <div className="text-center py-12">
                <p className="text-olive">Loading RSVPs...</p>
              </div>
            ) : rsvps.length === 0 ? (
              <div className="text-center py-12 editorial-card">
                <p className="text-forest/70">No RSVPs have been submitted yet.</p>
              </div>
            ) : (
              <div className="admin-table-wrap">
                <Table data-testid="rsvp-table" className="admin-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Guest</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Meal</TableHead>
                      <TableHead>Welcome Party</TableHead>
                      <TableHead>Plus One</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rsvps.map((rsvp) => (
                      <TableRow key={rsvp.id} data-testid={`rsvp-row-${rsvp.id}`}>
                        <TableCell className="font-medium">
                          <div>{rsvp.name}</div>
                          {rsvp.dietary_restrictions && (
                            <div className="text-xs text-olive mt-1">Diet: {rsvp.dietary_restrictions}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{rsvp.email}</TableCell>
                        <TableCell>
                          <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${
                            rsvp.attendance === 'accept' 
                              ? 'bg-olive/20 text-olive' 
                              : 'bg-taupe/30 text-forest/70'
                          }`}>
                            {rsvp.attendance === 'accept' ? 'Yes' : 'No'}
                          </span>
                        </TableCell>
                        <TableCell>{rsvp.meal_choice || '—'}</TableCell>
                        <TableCell>
                          {rsvp.welcome_party ? (
                            <span className={`inline-block px-2 py-1 text-xs uppercase tracking-wider ${
                              rsvp.welcome_party === 'yes' 
                                ? 'bg-olive/20 text-olive' 
                                : 'bg-taupe/30 text-forest/70'
                            }`}>
                              {rsvp.welcome_party === 'yes' ? 'Yes' : 'No'}
                            </span>
                          ) : '—'}
                        </TableCell>
                        <TableCell>
                          {rsvp.has_plus_one && rsvp.plus_one ? (
                            <div className="text-sm">
                              <div className="font-medium">{rsvp.plus_one.name}</div>
                              <div className="text-xs text-olive">
                                {rsvp.plus_one.attendance === 'accept' ? (
                                  <>
                                    {rsvp.plus_one.meal_choice} · WP: {rsvp.plus_one.welcome_party === 'yes' ? 'Yes' : 'No'}
                                  </>
                                ) : 'Declined'}
                              </div>
                            </div>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-sm text-forest/60">
                          {formatDate(rsvp.submitted_at)}
                        </TableCell>
                        <TableCell>
                          <button
                            onClick={() => handleDeleteRsvp(rsvp)}
                            data-testid={`delete-rsvp-${rsvp.id}`}
                            className="text-sm text-olive hover:text-forest underline"
                          >
                            Delete
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="messages">
            {contactRequests.length === 0 ? (
              <div className="editorial-card text-center py-12">
                <p className="text-forest/70">No guest-list questions have been submitted.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {contactRequests.map((request) => {
                  const status = request.status || 'pending';
                  return (
                  <article key={request.id} className={`editorial-card admin-message-card ${status === 'resolved' ? 'admin-message-card--resolved' : ''}`}>
                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                      <div>
                        <div className="admin-message-card__title">
                          <h3 className="admin-display text-2xl text-forest">{request.name}</h3>
                          <span className={`admin-chip ${status === 'resolved' ? 'admin-chip--resolved' : 'admin-chip--pending'}`}>
                            {status}
                          </span>
                        </div>
                        <a
                          href={`mailto:${request.email}`}
                          className="text-sm text-olive underline underline-offset-4"
                        >
                          {request.email}
                        </a>
                      </div>
                      <p className="text-sm text-forest/55">{formatDate(request.submitted_at)}</p>
                    </div>
                    <p className="mt-5 text-base leading-relaxed text-forest/80 whitespace-pre-wrap">
                      {request.message || 'No additional message was included.'}
                    </p>
                    <div className="admin-message-card__actions">
                      <button
                        onClick={() => handleUpdateMessageStatus(request.id, status === 'resolved' ? 'pending' : 'resolved')}
                        className="btn-outline"
                      >
                        {status === 'resolved' ? 'Reopen' : 'Mark resolved'}
                      </button>
                      <button onClick={() => handleDeleteMessage(request.id)} className="admin-text-action admin-text-action--danger">
                        Delete
                      </button>
                    </div>
                  </article>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <div className="space-y-8">
              {/* Feature Toggles */}
              <div className="editorial-card">
                <h3 className="admin-display text-2xl text-forest mb-2">Website Visibility</h3>
                <p className="text-sm text-forest/60 mb-8">
                  Control which sections are visible on the public website. Use these toggles to enable sections when you're ready for guests to access them.
                </p>
                
                <div className="space-y-6">
                  {/* Enable RSVP Toggle */}
                  <div className="admin-setting-row">
                    <div className="space-y-1">
                      <Label htmlFor="show_rsvp" className="text-base font-medium text-forest cursor-pointer">
                        Enable RSVP
                      </Label>
                      <p className="text-sm text-forest/60">
                        Show the RSVP page and navigation link so guests can respond to your invitation.
                      </p>
                    </div>
                    <Switch
                      id="show_rsvp"
                      data-testid="toggle-rsvp"
                      checked={siteSettings.show_rsvp}
                      onCheckedChange={(checked) => handleUpdateSettings('show_rsvp', checked)}
                      disabled={settingsLoading}
                      className="data-[state=checked]:bg-forest"
                    />
                  </div>
                  
                  {/* Enable Registry Toggle */}
                  <div className="admin-setting-row">
                    <div className="space-y-1">
                      <Label htmlFor="show_registry" className="text-base font-medium text-forest cursor-pointer">
                        Enable Registry
                      </Label>
                      <p className="text-sm text-forest/60">
                        Show the Registry page and navigation link for gift contributions.
                      </p>
                    </div>
                    <Switch
                      id="show_registry"
                      data-testid="toggle-registry"
                      checked={siteSettings.show_registry}
                      onCheckedChange={(checked) => handleUpdateSettings('show_registry', checked)}
                      disabled={settingsLoading}
                      className="data-[state=checked]:bg-forest"
                    />
                  </div>
                </div>
              </div>
              
              {/* Current Status Summary */}
              <div className="editorial-card">
                <h3 className="admin-display text-2xl text-forest mb-6">Current Status</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="admin-status-card">
                    <div className={`admin-status-dot ${siteSettings.show_rsvp ? 'admin-status-dot--visible' : ''}`} />
                    <p className="text-sm font-medium text-forest">RSVP</p>
                    <p className="text-xs text-forest/60 mt-1">
                      {siteSettings.show_rsvp ? 'Visible to guests' : 'Hidden (Save the Date mode)'}
                    </p>
                  </div>
                  <div className="admin-status-card">
                    <div className={`admin-status-dot ${siteSettings.show_registry ? 'admin-status-dot--visible' : ''}`} />
                    <p className="text-sm font-medium text-forest">Registry</p>
                    <p className="text-xs text-forest/60 mt-1">
                      {siteSettings.show_registry ? 'Visible to guests' : 'Hidden (Save the Date mode)'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Instructions */}
              <div className="editorial-card border-l-4 border-olive">
                <h4 className="text-sm uppercase tracking-[0.15em] text-olive mb-3">How to Use</h4>
                <ul className="text-sm text-forest/70 space-y-2">
                  <li>• <strong>Save the Date Phase:</strong> Keep both toggles OFF. Guests can view basic wedding info only.</li>
                  <li>• <strong>RSVP Phase:</strong> Turn ON the RSVP toggle when you're ready to collect responses.</li>
                  <li>• <strong>Registry Phase:</strong> Turn ON the Registry toggle when you want guests to see your registry.</li>
                  <li>• Changes take effect immediately - guests will see the updated navigation on their next page load.</li>
                </ul>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </section>
    </div>
  );
}
